import { useTranslation } from 'react-i18next';

import { useConfirmPopup } from 'components/popups/useConfirmPopup';
import { useLoginPopup } from 'features/account';
import { useAlertPopup } from 'hooks/useAlertPopup';
import { usePopup } from 'hooks/usePopup';
import { useAuth } from 'providers/AuthProvider';
import { useCurrentUser } from 'providers/cache/cachedDataHooks';
import { CampaignDto } from 'services/campaign/campaignService.dto';
import { ItemWithAuctionDto } from 'services/item/withAuctionsData';
import { clearItemFormDraft, getItemFormDraft, isItemFormDraftMeaningful, ItemFormDraft } from 'utils/formDraftStorage';

import AddItemForm from './AddItemForm';
import EditItemForm from './EditItemForm';

export const useItemPopup = () => {
  const { t } = useTranslation('addItem');
  const { logged } = useAuth();
  const { currentUser, waitForCurrentUser } = useCurrentUser();
  const { showLoginPopup } = useLoginPopup();
  const { showPopup, hidePopup } = usePopup();
  const { showConfirmPopup } = useConfirmPopup();
  const { showAlertPopup } = useAlertPopup();

  const handleCloseAdd = () => hidePopup();
  const handleCloseEdit = () => showConfirmPopup({ msg: t('quit-form-msg'), onYes: hidePopup });

  const openAddItemForm = (campaign?: CampaignDto, draft?: ItemFormDraft) => {
    showPopup(<AddItemForm supportedCampaign={campaign} draft={draft} />, {
      onClose: handleCloseAdd,
      paddingVariant: 'FORM',
    });
  };

  const openAddItemWithDraftPrompt = (campaign?: CampaignDto, userId = currentUser?.id) => {
    const draft = userId ? getItemFormDraft(userId) : undefined;

    if (userId && isItemFormDraftMeaningful(draft)) {
      showAlertPopup({
        header: t('resume-draft.title'),
        description: t('resume-draft.message'),
        firstButton: {
          label: t('resume-draft.continue-btn'),
          onClick: () => openAddItemForm(campaign, draft),
        },
        secondButton: {
          label: t('resume-draft.start-new-btn'),
          onClick: () => {
            clearItemFormDraft(userId);
            openAddItemForm(campaign);
          },
        },
        testId: 'resume-item-draft-alert',
      });
      return;
    }

    openAddItemForm(campaign);
  };

  const showAddItemPopup = (campaign?: CampaignDto) => {
    if (!logged) {
      showLoginPopup({
        onLogged: async () => {
          const user = await waitForCurrentUser();
          openAddItemWithDraftPrompt(campaign, user?.id);
        },
      });
      return;
    }

    openAddItemWithDraftPrompt(campaign);
  };

  const showEditItemPopup = (item: ItemWithAuctionDto, campaign?: CampaignDto, onItemSaved?: () => void) => {
    const handleSaved = () => {
      hidePopup();
      onItemSaved && onItemSaved();
    };

    const openEdit = () =>
      showPopup(<EditItemForm item={item} supportedCampaign={campaign} onItemSaved={handleSaved} />, {
        onClose: handleCloseEdit,
        paddingVariant: 'FORM',
      });

    if (!logged) showLoginPopup({ onLogged: openEdit });
    else openEdit();
  };

  return { showAddItemPopup, showEditItemPopup };
};
